import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconButton, Stack, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IDictionaryPopup } from './dictionary.popup.tsx';
import { configsData } from '../../services/configsData/configsData.service.ts';
import { Observable, Subscription } from 'rxjs';
import { Table } from '../table/table.component.tsx';
import { Column, Footer, SFooter } from '../table/components/common.components.tsx';
import { safeStartCellEditMode } from '../table/functions/safeStartCellEditMode.ts';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { GridColDef } from '@mui/x-data-grid-pro';
import { GridRowParams } from '@mui/x-data-grid/models/params/gridRowParams';
import { GridRowModel } from '@mui/x-data-grid/models/gridRows';
import { viewCells } from './cells/views/viewCells.ts';
import {
  dictionaryState,
  DICT_SLOT_PLACEHOLDER_KEY,
  padWithEmptySlots,
} from './states/dictionary.state.ts';
import { deletionConfirmationPopup } from './deletionConfirmation/deletionConfirmation.instance.ts';
import { viewRows } from './cells/views/viewRows.ts';

export const ContentPopup: FC<IContentPopup> = ({
  dictionaryType,
  limit,
  columns,
  emptyRow,
  editNewCell,
  validateFields,
  nameAlias,
  popupTitle,
  recordDeletetionDisabled,
  customAddRowHandler,
}) => {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState<GridRowModel[]>();
  const [selectedRow, setSelectedRow] = useState<GridRowParams['row']>();
  const [disabledDelete, setDisabledDelete] = useState<boolean>(false);
  const [disableEditing, setDisableEditing] = useState<string[]>([]);
  // Captured from onRowsCountChange — onRowClick doesn't receive apiRef
  // directly via MUI's signature, so we hold the latest one in a ref.
  const apiRefHolder = useRef<any>(null);
  // Deferred (setTimeout) "open the name cell for editing" callbacks — tracked
  // so they can be cancelled on unmount, and so a stale one never fires against
  // a grid that has since re-rendered/closed (WA-3618).
  const editModeTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    let dictionarySub: Subscription;
    if (dictionaryType) {
      dictionarySub = (configsData.sub[dictionaryType]() as Observable<any>).subscribe((data) => {
        const updatedData = data.map((el: { [x: string]: any; label: string }) => {
          const { label, ...rest } = el;
          return { [nameAlias || 'name']: label, ...rest };
        });
        // Limited (slot-mode) dictionaries map to a fixed set of N columns in
        // FakturaAssistKonfiguration. Backend returns only filled slots —
        // we pad the rest as placeholders so all N positions stay visible
        // and the user can target a specific slot when adding a value
        // (WA-3506: the legacy "first free slot" behaviour was misread as
        //  silent renumbering of existing slots).
        const finalData =
          limit && limit > 0
            ? padWithEmptySlots(updatedData, limit, nameAlias || 'name', emptyRow)
            : updatedData;
        dictionaryState.pub.initData({
          data: finalData,
          limit,
          nameAlias,
          emptyRow,
          validateFields,
          dictionaryType,
        });
      });
      configsData.pub.common([dictionaryType!], true);
    }
    const dictionaryStateSub = dictionaryState.sub.state().subscribe(({ data, selectedRow }) => {
      setTableData(data);
      setSelectedRow(selectedRow);
    });
    const disabledDeleteSub = dictionaryState.sub.disableDelete().subscribe(setDisabledDelete);
    const disableEditingSub = dictionaryState.sub.disableEditing().subscribe(setDisableEditing);
    return () => {
      dictionaryState.pub.clearState();
      if (dictionarySub) dictionarySub.unsubscribe();
      dictionaryStateSub.unsubscribe();
      disabledDeleteSub.unsubscribe();
      disableEditingSub.unsubscribe();
      editModeTimers.current.forEach(clearTimeout);
      editModeTimers.current.clear();
    };
  }, []);

  const addRowHandler = useCallback(() => {
    if (customAddRowHandler) return customAddRowHandler();
    const newRow: GridRowParams['row'] =
      emptyRow ||
      columns.reduce((acc: GridRowParams['row'], item) => {
        acc[item.field] = null;
        return acc;
      }, {});
    dictionaryState.pub.addRow(newRow);
  }, [columns, emptyRow, customAddRowHandler]);

  const disableDelCustomly = useMemo<boolean>(() => {
    if (recordDeletetionDisabled && selectedRow) {
      return recordDeletetionDisabled(selectedRow);
    }
    return false;
  }, [recordDeletetionDisabled, selectedRow]);

  // In limited (slot-mode) mode the user adds values by clicking directly on
  // an empty slot row instead of pressing a global "+" button. This wraps
  // the consumer-supplied columns so the slot's name cell renders an "Empty"
  // hint when the row is a placeholder — keeping the consumer column config
  // untouched (call-sites in article.tab.tsx, customer.tab.tsx etc. don't
  // need to know about slot-mode at all).
  const slotMode = !!limit;
  const displayColumns: GridColDef[] = useMemo(() => {
    if (!slotMode) return columns;
    const nameField = nameAlias || 'name';
    return columns.map((col) => {
      if (col.field !== nameField) return col;
      const existingRenderer = col.renderCell;
      return {
        ...col,
        renderCell: (params) => {
          if (params.row?.[DICT_SLOT_PLACEHOLDER_KEY]) {
            return <SPlaceholderHint>{t('common.empty')}</SPlaceholderHint>;
          }
          return existingRenderer
            ? existingRenderer(params)
            : params.formattedValue ?? params.value;
        },
      };
    });
  }, [columns, slotMode, nameAlias, t]);

  return (
    <>
      <Stack
        direction='row'
        spacing={2}
        sx={{
          mb: 1,
        }}
      >
        {!slotMode && (
          <IconButton
            disabled={!tableData}
            color='primary'
            size='large'
            title={t('common.add')}
            onClick={addRowHandler}
          >
            <AddIcon />
          </IconButton>
        )}
        <IconButton
          disabled={
            !tableData ||
            tableData.length === 0 ||
            !selectedRow ||
            !!selectedRow?.[DICT_SLOT_PLACEHOLDER_KEY] ||
            disableDelCustomly ||
            (disabledDelete as boolean)
          }
          color='primary'
          size='large'
          title={t('common.remove')}
          onClick={() => dictionaryState.pub.deleteRow()}
        >
          <RemoveIcon />
        </IconButton>
      </Stack>
      <Table
        heightOffset={300}
        loading={!tableData}
        data={(tableData || []) as []}
        onRowClick={(e) => {
          if (slotMode && e.row?.[DICT_SLOT_PLACEHOLDER_KEY]) {
            // Click on an empty slot: immediately open the name cell for
            // editing on that exact slot id. No implicit "first free slot"
            // selection — the slot the user picks is the slot we save into.
            const timer = setTimeout(() => {
              editModeTimers.current.delete(timer);
              safeStartCellEditMode(
                apiRefHolder.current?.current,
                e.row.id as string,
                editNewCell || nameAlias || 'name',
              );
            }, 0);
            editModeTimers.current.add(timer);
            return;
          }
          dictionaryState.pub.selectRow(e.row);
        }}
        rowSelectionModel={selectedRow?.id}
        onRowsCountChange={(rows, prevRows, apiRef) => {
          apiRefHolder.current = apiRef;
          if (rows.length > prevRows.length) {
            const newRow = rows?.[rows.length - 1];
            if (newRow && !newRow[nameAlias || 'name']) {
              // // fixes opening of cell after closing previous cell
              const timer = setTimeout(() => {
                editModeTimers.current.delete(timer);
                safeStartCellEditMode(
                  apiRef?.current,
                  newRow.id as string,
                  editNewCell || nameAlias || 'name',
                );
              }, 0);
              editModeTimers.current.add(timer);
            }
          }
        }}
        isCellEditable={(e) => !disableEditing.includes(String(e.id))}
        {...{ viewCells, viewRows }}
      >
        {
          displayColumns.map((params, i) => (
            <Column key={i} sortable={false} resizable={false} flex={1} {...params} />
          )) as any
        }
        {limit ? (
          <Footer>
            <SCustomFooter>
              {t('common.max')}: {limit}
            </SCustomFooter>
          </Footer>
        ) : (
          <Fragment />
        )}
      </Table>
      <deletionConfirmationPopup.Component {...{ popupTitle }} />
    </>
  );
};

const SPlaceholderHint = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}));

type IContentPopup = Pick<
  IDictionaryPopup['params'],
  | 'columns'
  | 'dictionaryType'
  | 'limit'
  | 'emptyRow'
  | 'editNewCell'
  | 'validateFields'
  | 'nameAlias'
  | 'popupTitle'
  | 'recordDeletetionDisabled'
  | 'customAddRowHandler'
>;

const SCustomFooter = styled(SFooter)({
  justifyContent: 'end',
});
